import {
  Clock,
  Package,
  Truck,
  MapPinLine,
  CheckCircle,
  Handshake,
  XCircle,
  PaperPlaneTilt,
} from "@phosphor-icons/react";

export const getStatusColor = (status: string | undefined) => {
  switch (status) {
    case "approved":
      return { bg: "#dcfce7", text: "#22c55e" };
    case "pending":
      return { bg: "#fef9c3", text: "#eab308" };
    case "declined":
      return { bg: "#fee2e2", text: "#ef4444" };
    default:
      return { bg: "initial", text: "initial" }; // Unset or undefined
  }
};

type StatusMeta = {
  label: string;
  description: string;
  color: string;
  icon: React.ElementType;
};

export function getOrderStatusMeta(status: string | undefined): StatusMeta {
  switch (status) {
    case "pending":
      return {
        label: "Order Received",
        description: "We’ve received your order and are waiting for payment.",
        color: "yellow",
        icon: Clock,
      };

    case "processing":
      return {
        label: "Processing",
        description: "Your order is being prepared by the merchant.",
        color: "yellow",
        icon: Clock,
      };

    case "packaged":
      return {
        label: "Packaged",
        description: "Your order has been packaged and is ready for dispatch.",
        color: "blue",
        icon: Package,
      };

    case "shipped":
    case "sentToVisaroHub":
      return {
        label: "Order Sent",
        description: "Your order has been sent to Visaro’s logistics hub.",
        color: "purple",
        icon: PaperPlaneTilt,
      };

    case "onRoute":
      return {
        label: "In Transit",
        description: "Your order is on the move to its next destination.",
        color: "cyan",
        icon: MapPinLine,
      };

    case "out_for_delivery":
    case "sentOutForDelivery":
      return {
        label: "Out For Delivery",
        description: "Your order is out for delivery. Expect it soon!",
        color: "indigo",
        icon: Truck,
      };

    case "readyForPickup":
      return {
        label: "Ready For Pickup",
        description:
          "Your order is ready for pickup at the designated location.",
        color: "indigo",
        icon: CheckCircle,
      };
    case "delivered":
      return {
        label: "Delivered",
        description: "Your order has been successfully delivered.",
        color: "green",
        icon: CheckCircle,
      };

    case "received":
    case "receivedByVisaroHub":
      return {
        label: "Received",
        description: "Visaro hub has received your order for processing.",
        color: "emerald",
        icon: Handshake,
      };

    case "cancelled":
    case "declined":
      return {
        label: status === "cancelled" ? "Cancelled" : "Declined",
        description:
          status === "cancelled"
            ? "This order was cancelled and will not be processed further."
            : "Unfortunately, the merchant declined this order.",
        color: "red",
        icon: XCircle,
      };

    case "refunded":
      return {
        label: "Refunded",
        description: "Your order has been refunded by our team.",
        color: "red",
        icon: XCircle,
      };

    case "completed":
      return {
        label: "Completed",
        description: "Your order is complete. Thanks for shopping with Visaro!",
        color: "green",
        icon: CheckCircle,
      };

    case "successful":
      return {
        label: "Successful",
        description:
          "Order completed successfully. Thanks for shopping with us!",
        color: "green",
        icon: CheckCircle,
      };

    case "pending_disbursement":
      return {
        label: "Awaiting Disbursement",
        description:
          "Your Pay Later order is awaiting loan approval. The merchant is paid once it's approved.",
        color: "yellow",
        icon: Clock,
      };

    case "fund_disbursed":
      return {
        label: "Funds Disbursed",
        description: "The loan was approved and the merchant has been paid.",
        color: "green",
        icon: CheckCircle,
      };

    default:
      return {
        label: status || "Unknown",
        description:
          "We’re updating your order status. Please check back soon.",
        color: "gray",
        icon: Clock,
      };
  }
}

export const colorVariants: Record<
  string,
  {
    container: string;
    iconWrapper: string;
    icon: string;
  }
> = {
  yellow: {
    container: "bg-yellow-100/50 border border-yellow-300 text-yellow-600",
    iconWrapper: "bg-yellow-500/10 rounded-full p-2",
    icon: "text-yellow-600",
  },
  blue: {
    container: "bg-blue-100/50 border border-blue-300 text-blue-600",
    iconWrapper: "bg-blue-500/10 rounded-full p-2",
    icon: "text-blue-600",
  },
  purple: {
    container: "bg-purple-100/50 border border-purple-300 text-purple-600",
    iconWrapper: "bg-purple-500/10 rounded-full p-2",
    icon: "text-purple-600",
  },
  indigo: {
    container: "bg-indigo-100/50 border border-indigo-300 text-indigo-600",
    iconWrapper: "bg-indigo-500/10 rounded-full p-2",
    icon: "text-indigo-600",
  },
  cyan: {
    container: "bg-cyan-100/50 border border-cyan-300 text-cyan-600",
    iconWrapper: "bg-cyan-500/10 rounded-full p-2",
    icon: "text-cyan-600",
  },
  green: {
    container: "bg-green-100/50 border border-green-300 text-green-600",
    iconWrapper: "bg-green-500/10 rounded-full p-2",
    icon: "text-green-600",
  },
  emerald: {
    container: "bg-emerald-100/50 border border-emerald-300 text-emerald-600",
    iconWrapper: "bg-emerald-500/10 rounded-full p-2",
    icon: "text-emerald-600",
  },
  red: {
    container: "bg-red-100/50 border border-red-300 text-red-600",
    iconWrapper: "bg-red-500/10 rounded-full p-2",
    icon: "text-red-600",
  },
  gray: {
    container: "bg-gray-100/50 border border-gray-300 text-gray-600",
    iconWrapper: "bg-gray-500/10 rounded-full p-2",
    icon: "text-gray-600",
  },
};
